import { useEffect, useState, useRef } from 'react'
import { useCurrentUser } from '@context/CurrentUserContext'
import useSentry from '@hooks/useSentry'
import gql from '@lib/gql'
import runAsync from '@lib/utils/runAsync'

interface Variables {
  [key: string]: string | number | boolean
}

interface UseQueryOptions {
  preventAutofetch?: boolean
  needsAuthentication?: boolean
}

interface Body {
  query: string,
  variables?: Variables
}

interface Headers {
  Authorization?: string
}

const useQuery = <Data extends unknown>(
  query: string,
  variables?: Variables,
  options?: UseQueryOptions,
) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Maybe<unknown>>(undefined)
  const [data, setData] = useState<Maybe<Data>>(undefined)
  const mounted = useRef<boolean>(true)

  const { currentUser, userAuthenticated } = useCurrentUser()
  const { captureException } = useSentry()

  const body: Body = { query }
  if (variables) body.variables = variables

  const headers: Headers = {}
  if (currentUser?.jwtToken) headers.Authorization = `Bearer ${currentUser.jwtToken}`

  const refetch = async () => {
    setLoading(true)
    setError(undefined)
    setData(undefined)
    await fetch()
  }

  const fetch = async () => {
    if (options?.needsAuthentication && !userAuthenticated) return

    try {
      const response = await gql.post('', { query, variables }, { headers })
      if (!mounted) return
      if (response.data.errors) {
        captureException(response.data.errors[0])
        setError(response.data.errors[0])
      } else {
        setData(response.data.data)
      }
    } catch (error: unknown) {
      captureException(error)
      if (mounted) setError(error)
    } finally {
      if (mounted) setLoading(false)
    }
  }

  useEffect(() => {
    if (!options?.preventAutofetch) runAsync(refetch)
    return () => { mounted.current = false }
  }, [])

  return { data, loading, error, refetch }
}

export default useQuery
